import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

import { loadStateOf } from '../../../core-components/theme/components/Screen';
import type { LoadState } from '../../../core-components/theme/components/Screen';
import { useDeadlinePassed } from '../../passcode-auth';
import { gateDecisionOf, gateStatusOf } from '../domain/onboardingProgress';
import type { WizardRouteState } from '../domain/onboardingProgress';

import { onboardingStatusQueryOptions } from './useOnboardingStatus';

export const GATE_WAIT_MS = 3_000;
export const GATE_RECHECK_MS = 30_000;

export type OnboardingGateView =
  | { readonly kind: 'checking'; readonly load: LoadState }
  | { readonly kind: 'redirect'; readonly to: string; readonly state: WizardRouteState }
  | { readonly kind: 'pass' };

export const useOnboardingGate = (): OnboardingGateView => {
  const location = useLocation();
  const [openedAt] = useState(() => Date.now());
  const waitedLong = useDeadlinePassed(openedAt + GATE_WAIT_MS);
  const settings = useQuery({
    ...onboardingStatusQueryOptions(),
    refetchInterval: (query) => (query.state.data === undefined && query.state.status === 'error' ? GATE_RECHECK_MS : false),
  });
  const status = gateStatusOf({ known: settings.data !== undefined, failed: settings.isError || settings.failureCount > 0 || settings.errorUpdateCount > 0, waitedLong });
  const decision = gateDecisionOf({ status, onboarding: settings.data }, location);
  return decision.kind === 'checking' ? { kind: 'checking', load: loadStateOf(settings) } : decision;
};
